import requestV2 from '@/service/request-v2'
import { normalizePageResult } from '@/service/adapters/page-result'
import type { PageQuery, PageResult } from '@/types/crud'

export interface StatisticalEmpFilters {
  channelId: string | number
  accessTime: string[]
  empIds: string | number
}

export interface StatisticalEmpRecord {
  empId: string | number
  deptName: string
  empName: string
  channelName: string
  pv: number
  uv: number
  ip: number
  duration: number
}

export interface StatisticalEmpSummary {
  pv: number
  uv: number
  ip: number
}

interface StatisticalEmpEmployee {
  id: string | number
  realName: string
}

interface StatisticalEmpChannel {
  rowId: string | number
  eventName: string
}

export interface StatisticalEmpFilterOptions {
  employees: StatisticalEmpEmployee[]
  channels: StatisticalEmpChannel[]
}

function buildParams(query: PageQuery<StatisticalEmpFilters>) {
  return {
    ...query.filters,
    pageNo: query.pageNo,
    pageSize: query.pageSize
  }
}

function requireArray<T>(payload: unknown, label: string): T[] {
  if (!Array.isArray(payload)) throw new Error(`${label}响应必须是数组`)
  return payload as T[]
}

function normalizeSummary(payload: unknown): StatisticalEmpSummary {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('汇总响应必须是对象')
  }
  const summary = payload as Partial<StatisticalEmpSummary>
  if (
    typeof summary.pv !== 'number' ||
    typeof summary.uv !== 'number' ||
    typeof summary.ip !== 'number'
  ) {
    throw new Error('汇总响应缺少有效的 pv、uv 或 ip')
  }
  return summary as StatisticalEmpSummary
}

async function requireExportFile(blob: Blob) {
  if (!blob.type.includes('application/json')) return blob
  const payload = JSON.parse(await blob.text()) as { message?: string }
  throw new Error(payload.message || '导出失败')
}

export async function fetchStatisticalEmpPage(
  query: PageQuery<StatisticalEmpFilters>,
  signal: AbortSignal
): Promise<PageResult<StatisticalEmpRecord>> {
  const payload = await requestV2.get<unknown>({
    url: '/main/liveStatisticalEmp/queryByCondition',
    params: buildParams(query),
    signal
  })
  return normalizePageResult<StatisticalEmpRecord>(payload)
}

export async function fetchStatisticalEmpSummary(
  filters: Partial<StatisticalEmpFilters>,
  signal: AbortSignal
): Promise<StatisticalEmpSummary> {
  const payload = await requestV2.get<unknown>({
    url: '/main/liveStatisticalDept/queryStatistical',
    params: {
      ...filters,
      pageNo: 1,
      pageSize: 1000
    },
    signal
  })
  return normalizeSummary(payload)
}

export async function fetchStatisticalEmpFilterOptions(): Promise<StatisticalEmpFilterOptions> {
  const [employeePayload, channelPayload] = await Promise.all([
    requestV2.get<unknown>({
      url: '/main/employee/queryEmpList'
    }),
    requestV2.get<unknown>({
      url: '/main/customerEvents/queryByCondition',
      params: { pageNo: 1, pageSize: 1000 }
    })
  ])

  return {
    employees: requireArray<StatisticalEmpEmployee>(
      employeePayload,
      '员工列表'
    ),
    channels: normalizePageResult<StatisticalEmpChannel>(channelPayload).records
  }
}

export function exportStatisticalEmp(query: PageQuery<StatisticalEmpFilters>) {
  return requestV2
    .get<Blob>({
      url: '/main/liveStatisticalEmp/download',
      params: buildParams(query),
      responseType: 'blob'
    })
    .then(requireExportFile)
}
